<template>
  <v-dialog v-model="dialog" :overlay="false" scrollable max-width="600px">
    <v-card tile flat>
      <v-app-bar flat dark color="primary">
        <v-app-bar-nav-icon><v-icon>fa-pound-sign</v-icon></v-app-bar-nav-icon>
        <v-toolbar-title v-if="duplicateEnquiry">Subsequent Enquiry</v-toolbar-title>
        <v-toolbar-title v-else>Funding Released</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click.native="closeDialog" dark>
          <v-icon>close</v-icon>
        </v-btn>
      </v-app-bar>
      <v-card flat tile class="ma-0 mb-5">
        <v-form lazy-validation>
          <v-container fluid grid-list-md>
            <v-flex v-if="duplicateEnquiry" xs12 class="px-3 pb-3">
              <v-select
                label="Product"
                v-model="updates.product_id"
                :items="products"
                item-value="id"
                item-text="name"
              ></v-select>
            </v-flex>
            <v-layout v-if="duplicateEnquiry">
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-text-field
                  label="Funding requested"
                  v-model="updates.funding_requested"
                  required
                  prefix="£"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-text-field
                  label="Funding offered"
                  v-model="updates.funding_offered"
                  required
                  prefix="£"
                  @keydown="formatCurrency"
                ></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-text-field
                  label="Funding obtained"
                  v-model="updates.funding_obtained"
                  required
                  prefix="£"
                  :onkeyup="formatCurrency()"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-text-field label="Monthly interest rate" v-model="updates.monthly_interest_rate"></v-text-field>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex xs12 sm4 class="pa-3">
                <v-menu
                  ref="fromMenu"
                  v-model="showDateFundingObtained"
                  :close-on-content-click="false"
                  :close-on-click="false"
                  transition="scale-transition"
                  offset-y
                  full-width
                  :nudge-right="40"
                  min-width="290px"
                  :return-value.sync="updates.date_funding_obtained"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      placeholder="Date funding obtained"
                      v-model="prettyDateFundingObtained"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="updates.date_funding_obtained"
                    no-title
                    scrollable
                    :min="minDateFunded"
                    :max="new Date().toISOString().substr(0, 10)"
                    @change="$refs.fromMenu.save(updates.date_funding_obtained)"
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="showDateFundingObtained = false">Cancel</v-btn>
                  </v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12 sm4 class="pa-3">
                <v-select
                  :items="feeTypes"
                  v-model="updates.fee_type"
                  label="Fee type"
                  single-line
                  menu-props="bottom"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm4 class="layout align-end pa-3">
                <v-switch v-model="packaged" label="Packaged"></v-switch>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex xs12 sm6 class="pa-3">
                <v-select
                  :items="bbbFeeTypes"
                  v-model="bbb_fee_type"
                  item-text="text"
                  item-value="value"
                  label="BBB Fee type"
                  single-line
                  menu-props="bottom"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm4 class="pa-3" v-if="updates.fee_type === 'Percentage'">
                <v-text-field label="Percentage" v-model="fee_percentage"></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pa-3">
                <v-text-field
                  label="Expected fee"
                  :readonly="updates.fee_type === 'Percentage'"
                  v-model="updates.expected_fee"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-text-field
                  label="Confirm borrower name"
                  v-model="updates.confirmed_company_name"
                  required
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md6 class="pa-3">
                <v-select
                  :items="borrowerTypes"
                  v-model="updates.confirmed_company_type"
                  label="Confirm borrower type"
                  single-line
                  menu-props="bottom"
                ></v-select>
              </v-flex>
            </v-layout>
            <v-flex xs12 sm6 md6 class="pa-3">
              <v-text-field
                label="Initial drawdown"
                prefix="£"
                v-model="updates.initial_drawdown"
                required
              ></v-text-field>
            </v-flex>
          </v-container>
        </v-form>
        <v-card-actions class="pr-3">
          <v-spacer></v-spacer>
          <v-btn color="darken-1" text @click.native="closeDialog">Close</v-btn>
          <v-btn :disabled="fundingObtainedCheck" v-if="duplicateEnquiry" color="accent" text @click="duplicate">
            Create
          </v-btn>
          <v-btn :disabled="fundingObtainedCheck" v-else color="accent" text @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import { DateTime } from 'luxon';
import productsApi from '@/api/products';
import { formatAsCurrency, parseCurrencyString } from '@/utils/formatters';

export default {
  name: 'enquiry-funding-released-dialog',
  props: ['show', 'enquiry', 'status_id', 'duplicateEnquiry'],
  data: () => ({
    isSaving: false,
    showDateFundingObtained: false,
    updates: {
      funding_requested: 0,
      funding_offered: 0,
      funding_obtained: 0,
      monthly_interest_rate: 0.0,
      date_funding_obtained: null,
      fee_type: null,
      packaged: false,
      bbb_fee_type: null,
      fee_percentage: null,
      expected_fee: 0.0,
      confirmed_company_name: null,
      confirmed_company_type: null,
      enquiry_status_id: null,
      product_id: null,
      initial_drawdown: 0
    },
    updateEnquiry: {},
    feeTypes: ['Fixed', 'Percentage', 'Tiered'],
    bbbFeeTypes: [
      { value: 'prime', text: 'Prime' },
      { value: 'sub_prime', text: 'Sub Prime' }
    ],
    borrowerTypes: [
      { value: 'limited_company', text: 'Limited Company' },
      { value: 'limited_partnership', text: 'Limited Partnership' },
      { value: 'partnership_less_than_equal_to_3', text: 'Partnership (3 or less)' },
      { value: 'partnership_greater_than_3', text: 'Partnership (more than 3)' },
      { value: 'public_company', text: 'Public Company' },
      { value: 'sole_trader', text: 'Sole Trader' },
      { value: 'charity_limited_by_guarantee', text: 'Charity Limited by Guarantee' },
      { value: 'other', text: 'Other' }
    ],
    products: []
  }),
  computed: {
    ...mapGetters(['CalculatedFee', 'SelectedCustomer']),
    bbb_fee_type: {
      get() {
        return this.updates.bbb_fee_type;
      },
      set(value) {
        this.updates.bbb_fee_type = value;
        this.recalculateFee();
      }
    },
    packaged: {
      get() {
        return this.updates.packaged;
      },
      set(value) {
        this.updates.packaged = value;
        this.recalculateFee();
      }
    },
    fee_percentage: {
      get() {
        return this.updates.fee_percentage;
      },
      set(value) {
        this.updates.fee_percentage = value;
        let fundingObtained = parseCurrencyString(this.updates.funding_obtained);
        this.updates.expected_fee = (fundingObtained * value) / 100;
      }
    },
    dialog: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      }
    },
    prettyDateFundingObtained: function() {
      return _.isNull(this.updates.date_funding_obtained)
        ? null
        : DateTime.fromISO(this.updates.date_funding_obtained).toLocaleString();
    },
    fundingObtainedCheck() {
      return (
        _.isEmpty(this.updates.funding_obtained) ||
        this.updates.funding_obtained < 1 ||
        _.isEmpty(this.updates.date_funding_obtained)
      );
    },
    minDateFunded() {
      return this.duplicateEnquiry ? this.enquiry.date_funding_obtained : '2016-05-01';
    }
  },
  methods: {
    ...mapActions(['storeCustomerEnquiry', 'duplicateCustomerEnquiry', 'getEnquiryCalculatedFee', 'addNotification']),
    closeDialog() {
      this.dialog = false;
    },
    recalculateFee() {
      _.merge(this.updateEnquiry, this.updates);
      this.getEnquiryCalculatedFee(this.updateEnquiry);
    },
    save() {
      this.isSaving = true;
      const updates = Object.assign({}, this.updates, {
        funding_obtained: parseCurrencyString(this.updates.funding_obtained),
        date_funding_obtained: this.formatDate(this.updates.date_funding_obtained),
        initial_drawdown: parseCurrencyString(this.updates.initial_drawdown)
      });

      delete updates.funding_requested;
      delete updates.funding_offered;

      this.storeCustomerEnquiry({
        id: this.enquiry.id,
        updates
      }).then(() => {
        this.isSaving = false;
        this.addNotification({ text: `enquiry updated` });
        this.dialog = false;
      });
    },
    duplicate() {
      this.isSaving = true;
      const updates = Object.assign({}, this.updates, {
        funding_requested: parseCurrencyString(this.updates.funding_requested),
        funding_offered: parseCurrencyString(this.updates.funding_offered),
        funding_obtained: parseCurrencyString(this.updates.funding_obtained),
        date_funding_obtained: this.formatDate(this.updates.date_funding_obtained),
        initial_drawdown: parseCurrencyString(this.updates.initial_drawdown)
      });

      this.duplicateCustomerEnquiry({
        id: this.enquiry.id,
        updates
      }).then(() => {
        this.isSaving = false;
        this.addNotification({ text: `enquiry duplicated` });
        this.dialog = false;
      });
    },
    formatDate(date) {
      let dateTime = new Date(date);
      return moment(dateTime).format('YYYY-MM-DD');
    },
    formatCurrency() {
      let value = this.updates.funding_obtained;
      const intValue = parseCurrencyString(value);

      this.updates.funding_obtained = formatAsCurrency(intValue);
      this.updates.funding_requested = formatAsCurrency(parseCurrencyString(this.updates.funding_requested));
      this.updates.funding_offered = formatAsCurrency(parseCurrencyString(this.updates.funding_offered));
      this.updates.initial_drawdown = formatAsCurrency(parseCurrencyString(this.updates.initial_drawdown));

      if (this.updates.fee_type === 'Percentage') {
        this.updates.expected_fee = (intValue * this.updates.fee_percentage) / 100;
      }
    }
  },
  watch: {
    CalculatedFee: function() {
      this.updates.fee_type = this.CalculatedFee.fee_type;
      this.updates.bbb_fee_type = this.CalculatedFee.bbb_fee_type;
      this.updates.fee_percentage = this.CalculatedFee.fee_percentage;
      this.updates.expected_fee = this.CalculatedFee.expected_fee;

      if (this.updates.packaged === true) {
        let fundingObtained = parseCurrencyString(this.updates.funding_obtained);
        this.updates.expected_fee += fundingObtained * 0.01;
      }
    },
    show: function() {
      if (this.duplicateEnquiry) {
        this.updates.funding_requested = 0;
        this.updates.funding_offered = 0;
        productsApi
          .getProductsByFunder(this.enquiry.product.provider.id)
          .then(({ data }) => (this.products = data.data));
      } else if (this.enquiry.initial_drawdown !== null) {
        this.initial_drawdown = this.enquiry.initial_drawdown;
      }

      this.products.push(this.enquiry.product);
      if (_.isNull(this.enquiry.expected_fee)) {
        this.getEnquiryCalculatedFee(this.enquiry);
        _.merge(this.updateEnquiry, this.enquiry);
      } else {
        this.updates.fee_type = this.enquiry.fee_type;
        this.updates.bbb_fee_type = this.enquiry.bbb_fee_type;
        this.updates.fee_percentage = this.enquiry.fee_percentage;
        this.updates.expected_fee = this.enquiry.expected_fee;
      }

      if (parseCurrencyString(this.enquiry.funding_obtained) > 0) {
        this.updates.funding_obtained = this.enquiry.funding_obtained;
      } else if (parseCurrencyString(this.enquiry.funding_offered) > 0) {
        this.updates.funding_obtained = this.enquiry.funding_offered;
      } else {
        this.updates.funding_obtained = this.enquiry.funding_requested;
      }

      this.updates.monthly_interest_rate = this.enquiry.monthly_interest_rate;
      this.updates.date_funding_obtained = this.duplicateEnquiry
        ? this.formatDate(Date())
        : this.enquiry.date_funding_obtained;
      this.updates.packaged = this.enquiry.packaged;
      this.updates.confirmed_company_name = _.isNull(this.enquiry.confirmed_company_name)
        ? this.SelectedCustomer.name
        : this.enquiry.confirmed_company_name;
      this.updates.confirmed_company_type = this.enquiry.confirmed_company_type;
      this.updates.enquiry_status_id = this.status_id === '' ? this.enquiry.status_id : this.status_id;
      this.updates.product_id = this.enquiry.product.id;
      this.formatCurrency();
    },
    updates: function() {
      _.merge(this.enquiry, this.updates);
    },
    'updates.funding_obtained': function(val) {
      if (this.duplicateEnquiry || this.enquiry.status.name !== 'FUNDING RELEASED') {
        this.updates.initial_drawdown = val;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  div.list__tile,
  .subheader {
    padding: 0 16px 0 0;
  }

  input,
  label {
    font-size: 14px;
  }
}

address {
  font-style: normal;
}
</style>
